import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

// ── Pages du thème (sitewebthemepage) ────────────────────────────────
// Le design réel d'un thème Zifek vit dans ses pages HTML + sa feuille
// de style. Ce hook charge les pages demandées (home, header, footer…)
// pour le thème actif du tenant, avec un cache mémoire partagé afin
// d'éviter de re-télécharger la même page sur chaque navigation.

export interface ThemePageContent {
  title: string;
  content: string;
}

const cache = new Map<string, ThemePageContent>();

function cacheKey(themeId: number, pageKey: string): string {
  return `${themeId}:${pageKey}`;
}

export interface UseThemePagesResult {
  pages: Record<string, ThemePageContent>;
  loading: boolean;
}

/**
 * Charge plusieurs pages du thème actif en une seule requête.
 * @param pageKeys clés à charger (ex: ['header','navmenu','footer'])
 */
export function useThemePages(pageKeys: string[]): UseThemePagesResult {
  const { theme } = useTenant();
  const themeId = theme?.idtheme ?? null;
  const keys = pageKeys.join(',');

  const [pages, setPages] = useState<Record<string, ThemePageContent>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!themeId) {
      setPages({});
      setLoading(false);
      return;
    }

    const list = keys ? keys.split(',').filter(Boolean) : [];
    if (list.length === 0) {
      setPages({});
      setLoading(false);
      return;
    }

    let cancelled = false;

    const resolved: Record<string, ThemePageContent> = {};
    const missing: string[] = [];
    list.forEach((k) => {
      const hit = cache.get(cacheKey(themeId, k));
      if (hit) resolved[k] = hit;
      else missing.push(k);
    });

    if (missing.length === 0) {
      setPages(resolved);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { data } = await supabase
          .from('sitewebthemepage')
          .select('page_key, title, content')
          .eq('idtheme', themeId)
          .in('page_key', missing);
        (data || []).forEach((row: { page_key: string; title: string | null; content: string | null }) => {
          const value: ThemePageContent = { title: row.title || '', content: row.content || '' };
          cache.set(cacheKey(themeId, row.page_key), value);
          resolved[row.page_key] = value;
        });
      } catch {
        /* on laisse les pages manquantes vides */
      }
      if (!cancelled) {
        setPages(resolved);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [themeId, keys]);

  return { pages, loading };
}

export function useThemePage(pageKey?: string): { page: ThemePageContent | null; loading: boolean } {
  const result = useThemePages(pageKey ? [pageKey] : []);
  const page = pageKey ? result.pages[pageKey] || null : null;
  return { page, loading: result.loading };
}